import React, {FC, useContext, useState} from "react";
import {Button, Modal} from "react-bootstrap";
import {SupportUserMatch, UserChats, usersService} from "services";
import {AuthContext} from "contexts";
import {ModerationUser} from "interfaces";
import {isModerationPackAvailable, UserStatuses} from "enums";
import {BiChat, BiHeart} from "react-icons/bi";
import {ModerationDownloadChatsBody} from "components/ModerationUserCard/components/ModerationDownloadChatsBody";
import {ModerationDownloadMatchesBody} from "components/ModerationUserCard/components/ModerationDownloadMatchesBody";

interface Props {
    user: ModerationUser
}

type ModalView = 'chats' | 'matches'

export const ModerationDownloadData: FC<Props> = ({user}) => {
    const {env} = useContext(AuthContext);

    const [isModalVisible, setIsModalVisible] = useState(false);
    const [modalView, setModalView] = useState<ModalView>('chats');
    const [data, setData] = useState<UserChats[]>([]);
    const [matchesData, setMatchesData] = useState<SupportUserMatch[]>([]);

    const getChats = async () => {
        if (!user || !env) {
            return;
        }

        const newData = await usersService.getAllChats(user.id, env)
        if (newData) {
            setModalView('chats')
            setData(newData)
            setIsModalVisible(true)
        }
    }

    const getMatches = async () => {
        if (!user || !env) {
            return;
        }

        const response = await usersService.getMatches(user.id, env)
        if (response?.matches) {
            setModalView('matches')
            setMatchesData(response.matches)
            setIsModalVisible(true)
        }
    }

    const downloadFile = () => {
        const payload = modalView === 'chats' ? data : {matches: matchesData};
        const dataStr = "data:text/json;charset=utf-8," + encodeURIComponent(JSON.stringify(payload));
        const downloadAnchorNode = document.createElement('a');
        downloadAnchorNode.setAttribute("href", dataStr);
        const suffix = modalView === 'chats' ? '' : '-matches';
        downloadAnchorNode.setAttribute("download", user.name + suffix + ".json");
        document.body.appendChild(downloadAnchorNode); // required for firefox
        downloadAnchorNode.click();
        downloadAnchorNode.remove();


    }

    const downloadData = async () => {
        if (!user || !env) {
            return;
        }

        const newData = await usersService.downloadProfile(user.id, env)
        if (newData) {
            setModalView('chats')
            setData(newData.chats.map(c => ({
                status: UserStatuses.Archived,
                name: '',
                key: c.matchId,
                messages: (c.messages ?? c.chatMessages ?? c.signals)
                    .map(m => ({
                        message: m.message ?? m.content ?? m.subject,
                        date: m.date ?? m.time,
                        isMyMessage: m.isMyMessage,
                        messageType: m.messageType
                    }))
            })))
            setIsModalVisible(true)
        }
    }

    const handleClose = () => {
        setData([])
        setMatchesData([])
        setIsModalVisible(false)
    }

    return <>
        <div className="d-flex flex-wrap align-items-center px-2">
            {user.chatsCount?.current > 0 &&
                <Button variant={'link'} className="d-flex align-items-center text-black-50"
                        onClick={isModerationPackAvailable(env) ? getChats : downloadData}>
                    <BiChat size={16} className="mx-1"/>
                    Чаты ({user.chatsCount?.current})
                </Button>}
            {user.matchesCount.allTime > 0 &&
                <Button variant={'link'} className="d-flex align-items-center text-black-50"
                        onClick={getMatches}>
                    <BiHeart size={16} className="mx-1"/>
                    Мэтчи
                </Button>
            }
        </div>
        <Modal show={isModalVisible} onHide={handleClose} className="modal-90vh">
            <Modal.Header closeButton>
                <Modal.Title>{modalView === 'chats' ? 'Чаты' : 'Мэтчи'}</Modal.Title>
            </Modal.Header>
            <Modal.Body className="modal-body-scroll">
                {modalView === 'chats' &&
                    <ModerationDownloadChatsBody user={user} chats={data} env={env}/>}
                {modalView === 'matches' &&
                    <ModerationDownloadMatchesBody matches={matchesData}/>}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={handleClose}>
                    Закрыть
                </Button>
                <Button variant="primary" onClick={downloadFile}>
                    Скачать все данные
                </Button>
            </Modal.Footer>
        </Modal>
    </>
}
